/* eslint-disable sonarjs/no-duplicate-string */
import { useEffect, useRef, useState } from "react";
/* eslint-disable camelcase */
import {
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  getAuth,
} from "firebase/auth";
import axios from "axios";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  STORAGE_AUTH_TOKEN,
  STORAGE_CLICK_ID,
  STORAGE_LANGUAGE_NAME,
  STORAGE_UTM_CAMPAIGN,
  STORAGE_UTM_MEDIUM,
  STORAGE_UTM_SOURCE,
} from "constants/variables";
import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";
import { useAuthStore } from "stores/auth";
import classNames from "classnames";
import { useGTM } from "hooks/useGTM";
import { SocialRegisterRequestType } from "types/Auth";
import url from "url/index";
import { useCatalogsStore } from "stores/catalogs";

// Utils
import { getClientId } from "utils/ga";

// Selectors
import { authApi } from "redux/auth/api";
import { userApi } from "redux/user/api";

// Icons
import FacebookIcon from "assets/icons/social/facebook-icon.svg?react";
import GoogleIcon from "assets/icons/social/google-icon.svg?react";
import TelegramIcon from "assets/icons/social/telegram-icon.svg?react";
import TwitterIcon from "assets/icons/social/x-icon.svg?react";

// Hooks
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCurrency } from "hooks/useCurrency";
import useTranslate from "hooks/useTranslate";
import LoaderSocial from "../LoaderSocial";

import { app } from "../../../../firebaseConfig";

import "./styles.sass";

export enum AuthMethods {
  PASSWORD = "password",
  PHONE = "phone",
  GOOGLE = "google",
  FACEBOOK = "facebook",
  TELEGRAM = "telegram",
  TIC_TOC = "tic-toc",
  YOUTUBE = "youtube",
  TWITTER = "twitter",
}

export function getBotUsername() {
  const { hostname } = window.location;
  const { VITE_APP_PROJECT_NAME } = import.meta.env;

  if (hostname.includes("stage.")) {
    if (VITE_APP_PROJECT_NAME === "banger") {
      return "BangerAuthBot"; // прод бенгер
    }

    if (VITE_APP_PROJECT_NAME === "glory") {
      return "TelStageBot"; // стейдж глори
    }
  }

  if (hostname.includes("dev.")) {
    if (VITE_APP_PROJECT_NAME === "banger") {
      return "BangerAuthBot"; // прод бенгер
    }

    if (VITE_APP_PROJECT_NAME === "glory") {
      return "DevGlorybot"; // дев глори
    }
  }

  if (VITE_APP_PROJECT_NAME === "banger") {
    return "BangerAuthBot"; // прод бенгер
  }
  if (VITE_APP_PROJECT_NAME === "glory") {
    return "GloryTelAuthBot"; // прод глорм
  }

  return "";
}

const SocialList = [
  {
    id: 0,
    name: AuthMethods.GOOGLE,
    icon: <GoogleIcon />,
    backgroundColor: "#F2F2F2",
  },
  {
    id: 1,
    name: AuthMethods.TELEGRAM,
    icon: <TelegramIcon />,
    backgroundColor: "#25A4E2",
  },
  {
    id: 2,
    name: AuthMethods.TWITTER,
    icon: <TwitterIcon />,
    backgroundColor: "#000000",
  },
  {
    id: 3,
    name: AuthMethods.FACEBOOK,
    icon: <FacebookIcon />,
    backgroundColor: "#0866FF",
  },
];

type Type = "signUp" | "signIn";

interface IProps {
  type: Type;
  currencyCode?: string;
}

export default function OAuthBlock({
  // eslint-disable-next-line unused-imports/no-unused-vars
  currencyCode,
  type = "signUp",
}: IProps) {
  const availableMethods = [
    AuthMethods.GOOGLE,
    // AuthMethods.TELEGRAM,
    // AuthMethods.FACEBOOK,
    // AuthMethods.TWITTER,
  ];

  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  const [loading, setIsLoading] = useState<any>(false);

  const [error, setError] = useState<any>("");
  const defaultCurrency = useCurrency();
  const containerRef = useRef<any>(null);
  const GTM = useGTM();
  const languages = useCatalogsStore((state) => state.languages);
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  // eslint-disable-next-line  unused-imports/no-unused-vars
  const [signIn, { isLoading }] = authApi.useLoginMutation();
  // eslint-disable-next-line  unused-imports/no-unused-vars
  const { refetch, isSuccess } = userApi.useGetProfileQuery(
    token ? "" : skipToken,
  );
  const navigate = useNavigate();
  const setIsAfterAuthCheck = useAuthStore(
    (state) => state.setIsAfterAuthCheck,
  );
  const lang = getLocalStorageItem(STORAGE_LANGUAGE_NAME) || "EN";

  const clickId = getLocalStorageItem(STORAGE_CLICK_ID);
  const utmMedium = getLocalStorageItem(STORAGE_UTM_MEDIUM);
  const utmSource = getLocalStorageItem(STORAGE_UTM_SOURCE);
  const utmCampaign = getLocalStorageItem(STORAGE_UTM_CAMPAIGN);

  const location = useLocation();
  const t = useTranslate();
  const language =
    (
      languages?.find((l) => l.isoCode.toLowerCase() === lang.toLowerCase()) ||
      {}
    ).id || 0;

  // eslint-disable-next-line unused-imports/no-unused-vars
  const [searchParams] = useSearchParams();

  const filterAvailableMethods = (authMethods, socialList) => {
    return authMethods
      .map((method) => socialList.find((item) => item.name === method))
      .filter(Boolean);
  };

  const socialItemStyle = classNames({
    OAuth__socialButtonGlory: VITE_APP_PROJECT_NAME === "glory",
    OAuth__socialButtonBanger: VITE_APP_PROJECT_NAME === "banger",
  });

  // GloryTelAuthBot - глори прод glory.casino
  // TelStageBot - глори стейдж stage.glorycoin.co
  // DevGlorybot - глори дев dev.glorycoin.co
  // BangerAuthBot - прод бенгера banger.casino
  // PurgatoryAuthBot - стейдж дивайн stage.techdivinecas.co
  // DivineAuthBot - прод дивайн divine.casino

  useEffect(() => {
    const container = containerRef.current;

    const timer = setTimeout(() => {
      if (!container) {
        // eslint-disable-next-line no-console
        console.error("Telegram widget container not found!");
        return;
      }

      container.innerHTML = "";

      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-widget.js?21";
      script.setAttribute("data-telegram-login", getBotUsername());

      const currencyCodeForm = currencyCode ?? defaultCurrency;

      const currencyParam = `currency=${currencyCodeForm}`;
      // const popupParam = type === "signUp" ? "popup=sign-up" : "popup=sign-in";
      // const queryString = `?${popupParam}&${currencyParam}`;

      // Эта часть - костыль для решении бага с авторегистрацией при заходе на Sign Up (нужно пофиксит в будущем)
      const fromParam = `from=${type === "signUp" ? "sign-up" : "sign-in"}`;
      const popupParam = type === "signUp" ? "popup=sign-up" : "popup=sign-in";
      const queryString = `?${popupParam}&${currencyParam}&${fromParam}`;

      script.setAttribute("data-auth-url", queryString);
      script.setAttribute("data-size", "large");
      script.setAttribute("data-request-access", "write");
      script.async = true;

      container.appendChild(script);

      container.telegramScript = script;
    }, 500);

    return () => clearTimeout(timer);
  }, [currencyCode, defaultCurrency, type]);

  useEffect(() => {
    if (token) {
      refetch();
    } else {
      setTimeout(() => {
        setIsAfterAuthCheck(true);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleGoogleAuth = async () => {
    if (type === "signUp") {
      GTM.onSocialRegistrationStart("google");
    } else if (type === "signIn") {
      GTM.onSocialAuthStart("google");
    }

    setIsLoading(SocialList[0]);
    const provider = new GoogleAuthProvider();
    const authGoogle = getAuth(app);

    const queryParams = new URLSearchParams(location.search);
    const rParam = queryParams.get("r");

    try {
      const result = await signInWithPopup(authGoogle, provider);
      const { user } = result;

      try {
        const accessToken = await user.getIdToken();
        const endpoint =
          type === "signUp" ? "/v1/auth/register" : "/v1/auth/login";

        const params: SocialRegisterRequestType = {
          socialToken: accessToken,
          authType: "social",
          currencyCode: currencyCode ?? defaultCurrency,
          languageId: language,
          depositUrl: `${window.location.origin.replace(":3000", ".ru")}/?popup=deposit`,
          welcomeBonusUrl:
            window.location.origin.replace(":3000", ".ru") +
            url.routes.info.welcomeBonus(),
          gaClientId: getClientId(),
        };

        if (clickId) {
          params.clickId = clickId;
        }
        if (utmMedium) {
          params.utmMedium = utmMedium;
        }
        if (utmSource) {
          params.utmSource = utmSource;
        }
        if (utmCampaign) {
          params.utmCampaign = utmCampaign;
        }
        if (rParam) {
          params.r = rParam;
        }

        const response = await axios.post(endpoint, params, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        const userEmail = response?.data?.login;
        const tempPassword = response?.data?.tempPassword;
        const authToken = response?.data?.authToken;

        if (type === "signIn") {
          GTM.onSocialAuthSuccess("google");
        }

        if (type === "signUp" && userEmail && tempPassword) {
          const submitData = await signIn({
            login: userEmail,
            password: tempPassword,
          });

          setLocalStorageItem("socialTempPassword", tempPassword);

          GTM.onSocialRegistrationSuccess("google");

          if ("data" in submitData) {
            navigate(
              `?popup=password-registration-confirm&userEmail=${encodeURIComponent(userEmail)}`,
            );
          }
        } else if (type === "signIn" && authToken) {
          setLocalStorageItem(STORAGE_AUTH_TOKEN, authToken);
          navigate("/");
        }
      } catch (errorGoogle: any) {
        switch (errorGoogle?.response?.data?.message) {
          case "This social network is not connected":
          case "User already exists":
            setError(t("user.already.exist.go.to.account"));
            break;
          case "This type of authorization is not currently supported":
            // eslint-disable-next-line sonarjs/no-duplicate-string
            setError(t("oauth.not.supported.now"));
            break;
          default:
            break;
        }

        setError(
          errorGoogle?.response?.data?.message ||
            errorGoogle?.response?.data?.error ||
            "Google authorization error",
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Ошибка при отправке токена 4:", error?.response?.data);
      setError("Google authorization error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookAuth = async () => {
    if (type === "signUp") {
      GTM.onSocialRegistrationStart("facebook");
    } else if (type === "signIn") {
      GTM.onSocialAuthStart("facebook");
    }

    setIsLoading(SocialList[3]);
    const provider = new FacebookAuthProvider();
    const authFacebook = getAuth(app);

    try {
      const result = await signInWithPopup(authFacebook, provider);

      const { user } = result;

      try {
        const accessToken = await user.getIdToken();
        const endpoint =
          type === "signUp" ? "/v1/auth/register" : "/v1/auth/login";

        const response = await axios.post(
          endpoint,
          {
            socialToken: accessToken,
            authType: "social",
            currencyCode: currencyCode ?? defaultCurrency,
            languageId: 6,
            depositUrl: "https://www.facebook.com/",
            welcomeBonusUrl: "https://www.facebook.com/",
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const userEmail = response?.data?.login;
        const tempPassword = response?.data?.tempPassword;
        const authToken = response?.data?.authToken;

        if (type === "signIn") {
          GTM.onSocialAuthSuccess("facebook");
        }

        if (type === "signUp" && userEmail && tempPassword) {
          const submitData = await signIn({
            login: userEmail,
            password: tempPassword,
          });

          GTM.onSocialRegistrationSuccess("facebook");

          setLocalStorageItem("socialTempPassword", tempPassword);

          if ("data" in submitData) {
            navigate(
              `?popup=password-registration-confirm&userEmail=${encodeURIComponent(userEmail)}`,
            );
          }
        } else if (type === "signIn" && authToken) {
          setLocalStorageItem(STORAGE_AUTH_TOKEN, authToken);
          navigate("/");
        }
      } catch (errorFacebook: any) {
        switch (errorFacebook?.response?.data?.message) {
          case "This social network is not connected":
          case "User already exists":
            setError(t("user.already.exist.go.to.account"));
            break;
          case "This type of authorization is not currently supported":
            setError(t("oauth.not.supported.now"));
            break;
          default:
            break;
        }

        setError(error?.response?.data?.message);
        // eslint-disable-next-line no-console
        console.error("Ошибка при отправке токена:", error?.response?.data);
      }

      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Ошибка при отправке токена 4:", error?.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwitterAuth = async () => {
    if (type === "signUp") {
      GTM.onSocialRegistrationStart("twitter");
    } else if (type === "signIn") {
      GTM.onSocialAuthStart("twitter");
    }

    setIsLoading(SocialList[2]);
    const provider = new TwitterAuthProvider();
    const authTwitter = getAuth(app);

    try {
      const result = await signInWithPopup(authTwitter, provider);

      const { user } = result;

      try {
        const accessToken = await user.getIdToken();
        const endpoint =
          type === "signUp" ? "/v1/auth/register" : "/v1/auth/login";

        const response = await axios.post(
          endpoint,
          {
            socialToken: accessToken,
            authType: "social",
            currencyCode: currencyCode ?? defaultCurrency,
            languageId: 6,
            depositUrl: "https://www.twitter.com/",
            welcomeBonusUrl: "https://www.twitter.com/",
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const userEmail = response?.data?.login;
        const tempPassword = response?.data?.tempPassword;
        const authToken = response?.data?.authToken;

        if (type === "signIn") {
          GTM.onSocialAuthSuccess("twitter");
        }

        if (type === "signUp" && userEmail && tempPassword) {
          const submitData = await signIn({
            login: userEmail,
            password: tempPassword,
          });

          GTM.onSocialRegistrationSuccess("twitter");

          setLocalStorageItem("socialTempPassword", tempPassword);

          if ("data" in submitData) {
            navigate(
              `?popup=password-registration-confirm&userEmail=${encodeURIComponent(userEmail)}`,
            );
          }
        } else if (type === "signIn" && authToken) {
          setLocalStorageItem(STORAGE_AUTH_TOKEN, authToken);
          navigate("/");
        }
      } catch (errorTwitter: any) {
        switch (errorTwitter?.response?.data?.message) {
          case "This social network is not connected":
          case "User already exists":
            setError(t("user.already.exist.go.to.account"));
            break;
          case "This type of authorization is not currently supported":
            setError(t("oauth.not.supported.now"));
            break;
          default:
            break;
        }

        setError(error?.response?.data?.message);
        // eslint-disable-next-line no-console
        console.error("Ошибка при отправке токена:", error?.response?.data);
      }

      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Ошибка при отправке токена 4:", error?.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerTelegramClick = () => {
    if (type === "signUp") {
      GTM.onSocialRegistrationStart("telegram");
    } else if (type === "signIn") {
      GTM.onSocialAuthStart("telegram");
    }

    const telegramButton = containerRef.current?.querySelector("button");
    if (telegramButton) {
      telegramButton.click();
    } else {
      setIsLoading(SocialList[1]);
    }
  };

  const handleAuthBySocial = {
    google: handleGoogleAuth,
    facebook: handleFacebookAuth,
    telegram: triggerTelegramClick,
    twitter: handleTwitterAuth,
    password: () => {},
    youtube: () => {},
  };

  if (
    availableMethods &&
    availableMethods.length === 1 &&
    availableMethods[0] === "password"
  ) {
    return null;
  }

  const telegramLayoutItemStyle = classNames({
    // eslint-disable-next-line camelcase
    OAuth__telegramLayoutWhiteGlory: VITE_APP_PROJECT_NAME === "glory",
    // eslint-disable-next-line camelcase
    OAuth__telegramLayoutWhiteBanger: VITE_APP_PROJECT_NAME === "banger",
  });

  return (
    <div className="OAuth__container">
      <div className="OAuth__socialList">
        {filterAvailableMethods(availableMethods, SocialList).map((social) => {
          // eslint-disable-next-line no-nested-ternary
          return loading?.id === social?.id ? (
            <div className={socialItemStyle} key={social.id}>
              <div className="OAuth__spinContainer">
                <LoaderSocial />
              </div>
            </div>
          ) : social?.id === SocialList[1]?.id ? (
            <div className="OAuth__socialButtonTelegram" key={social.id}>
              <div className="OAuth__telegramContentWrapper">
                <div
                  className={telegramLayoutItemStyle}
                  style={{ backgroundColor: social.backgroundColor }}
                />

                {/* Телеграм виджет внутри кнопки */}
                <div
                  ref={containerRef}
                  className="OAuth__telegramContainer"
                  id="telegram-login-widget"
                />

                <div className="OAuth__telegramIconWrapper">
                  <TelegramIcon />
                </div>
              </div>
            </div>
          ) : (
            <div
              key={social.id}
              onClick={handleAuthBySocial[social.name]}
              className={`${socialItemStyle}__${social.name}`}
              style={{ backgroundColor: social.backgroundColor }}
            >
              {social.icon}
            </div>
          );
        })}
      </div>

      {error && <div className="OAuth__errorBlock">{error}</div>}
    </div>
  );
}
